/**
 * PeerIsSubscribedToExclusiveContentUseCase
 *
 * Read whether the peer is subscribed to the current user's exclusive
 * content — i.e. the inverse of `selectHasActiveSubscription(state, peerId)`.
 * Used by the chat-page mutual-subscription gate: the composer is enabled
 * iff either the current user is subscribed to the peer OR the peer is
 * subscribed to the current user.
 *
 * Flutter source: lib/data/data_sources/content_subscriptions_data_source.dart:51-78
 *   — `peerIsSubscribedToExclusiveContent(userId)`. The query lives in the
 *   Flutter data source; on web the same query lives in `firestoreService`
 *   and this use case routes through `ExclusiveContentSubscribersService`
 *   so the use case stays pure-domain (does not touch `firestoreService`
 *   directly, does not dispatch to Redux).
 *
 * Pure-domain contract:
 *   - Does NOT touch `firestoreService` directly — goes through the service.
 *   - Does NOT dispatch to Redux — gate semantics live in the consuming hook.
 */

import { ExclusiveContentSubscribersService } from '@/domain/services/exclusiveContentSubscription/exclusiveContentSubscribersService';

export class PeerIsSubscribedToExclusiveContentUseCase {
  private service: ExclusiveContentSubscribersService;

  constructor(
    service: ExclusiveContentSubscribersService = ExclusiveContentSubscribersService.getInstance(),
  ) {
    this.service = service;
  }

  /**
   * Check whether the given peer has an active or cancelled subscription
   * pointing at the current user. Pass-through to
   * `ExclusiveContentSubscribersService.peerIsSubscribedToCurrentUser`.
   *
   * @param peerId The peer user's firebase id.
   * @returns `true` iff the peer is a subscriber of the current user.
   */
  async execute(peerId: string): Promise<boolean> {
    return this.service.peerIsSubscribedToCurrentUser(peerId);
  }
}
